import type { Role, User } from "./model.ts";

export type Permission =
  | "view" // dashboards, readings, reports
  | "control" // switch outputs, start/stop programs
  | "edit-rules" // farm rules, schedules, pumping programs, device safety rules
  | "override-safety-rules" // turn a board's own safety rule off / on / reset from the app (one playing up)
  | "setup-devices" // add devices, port set-up, names
  | "manage-users" // invite and remove users in their own account
  | "billing"
  | "fms-admin"; // FMS staff: every account, go-live, terms

const GRANTS: Record<Role, Permission[]> = {
  owner: ["view", "control", "edit-rules", "override-safety-rules", "setup-devices", "manage-users", "billing"],
  manager: ["view", "control", "edit-rules", "setup-devices", "manage-users"],
  operator: ["view", "control"],
  viewer: ["view"],
  installer: ["view", "setup-devices", "override-safety-rules"],
  "fms-admin": ["view", "control", "edit-rules", "override-safety-rules", "setup-devices", "manage-users", "billing", "fms-admin"],
};

export function can(user: Pick<User, "role">, permission: Permission): boolean {
  return GRANTS[user.role].includes(permission);
}

/**
 * Roles a user may hand out when inviting: only roles whose permissions they hold themselves.
 * Not "installer" for a manager: installers can turn board safety rules off and managers can't,
 * so a manager could otherwise give that to someone else (or a second login of their own).
 */
export function assignableRoles(user: Pick<User, "role">): Role[] {
  const all: Role[] = ["owner", "manager", "operator", "viewer", "installer"];
  if (user.role === "fms-admin") return all;
  if (user.role === "owner") return ["manager", "operator", "viewer", "installer"];
  if (user.role === "manager") return all.filter((r) => r !== "owner" && r !== "manager" && GRANTS[r].every((p) => GRANTS.manager.includes(p)));
  return [];
}
